import type { SubTradeRequestTypes } from '@generated/graphql';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { formatMoneyValueCentsWithDecimals } from '@utils/moneyFormatting';
import { Grid, Tooltip, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';

type RequestedTradeInfo = {
  type: SubTradeRequestTypes;
  isSellAll: boolean;
  moneyAllocatedCents: number;
  settlementDate: string;
};

const ChangeTooltip = ({
  change,
  original,
  total = false,
  flip = false,
  isSellAll = false,
  requestedTradeInfo = [],
}: {
  change: number;
  original: number;
  total?: boolean;
  flip?: boolean;
  isSellAll?: boolean;
  requestedTradeInfo?: RequestedTradeInfo[];
}) => {
  const { t } = useTranslation(['components']);

  const isPositive = () => {
    if (change > 0 && !flip) {
      return true;
    }
    if (change > 0 && flip) {
      return false;
    }
    if (change < 0 && !flip) {
      return false;
    }
    return true;
  };

  const getTooltipText = () => {
    const tooltipLines: string[] = [];

    tooltipLines.push(
      t('portfolioTable.original', {
        amount: formatMoneyValueCentsWithDecimals(original),
      }),
    );
    requestedTradeInfo.forEach((trade) => {
      const direction = trade.isSellAll
        ? t('portfolioTable.requestedSellAll')
        : trade.type === 'BUY'
          ? t('portfolioTable.requestedBuy')
          : t('portfolioTable.requestedSell');

      const timing = trade.settlementDate !== 'N/A' ? trade.settlementDate : '';

      tooltipLines.push(
        timing
          ? `${direction} — ${t('portfolioTable.settles')} ${timing}: ${formatMoneyValueCentsWithDecimals(trade.moneyAllocatedCents)}`
          : `${direction} — ${t('portfolioTable.settling')}: ${formatMoneyValueCentsWithDecimals(trade.moneyAllocatedCents)}`,
      );
    });

    // 2) If the user has entered an initiated trade
    if (change !== 0) {
      const buyOrSell = isPositive()
        ? t('portfolioTable.initiatedBuy')
        : isSellAll
          ? t('portfolioTable.initiatedSellAll')
          : t('portfolioTable.initiatedSell');

      tooltipLines.push(
        `${total ? `${t('portfolioTable.total')} ` : ''}${buyOrSell}: ${formatMoneyValueCentsWithDecimals(
          Math.abs(change),
        )}`,
      );
    }

    // If no lines added, fall back to an empty string
    return tooltipLines.join('\n');
  };

  const tooltipText = getTooltipText();

  return (
    <>
      {change !== 0 ? (
        <Grid>
          <Tooltip
            title={<div style={{ whiteSpace: 'pre-line' }}>{tooltipText}</div>}
          >
            {change > 0 ? (
              <ArrowUpwardIcon color='success' sx={{ fontSize: '14px' }} />
            ) : (
              <ArrowDownwardIcon color='error' sx={{ fontSize: '14px' }} />
            )}
          </Tooltip>
        </Grid>
      ) : requestedTradeInfo.length ? (
        <Tooltip
          title={
            <Typography
              variant='bodySmall'
              component='p'
              sx={{ color: 'white', whiteSpace: 'pre-line' }}
            >
              {tooltipText}
            </Typography>
          }
          placement='top'
        >
          <FiberManualRecordIcon
            color='info'
            sx={{ fontSize: '14px', color: '#465CC8' }}
          />
        </Tooltip>
      ) : (
        <FiberManualRecordIcon color='disabled' sx={{ fontSize: '14px' }} />
      )}
    </>
  );
};

export default ChangeTooltip;
